'use client'

import { useMedia } from '@/hooks'
import { cn } from '@/lib/utils'
import { Typo3Image } from '@/types'
import { Skeleton } from '@/ui/components'
import { getBlurhash } from '@/utils'
import { MEDIA } from '@spot'
import Image, { ImageProps } from 'next/image'
import { memo } from 'react'
import Div from '../Base/Div'

// TODO: type this
// @ts-expect-error expected error
export interface ImgProps
  extends Omit<ImageProps, 'src' | 'objectFit' | 'alt' | 'priority'>,
    Omit<ImageProps, 'src' | 'objectFit' | 'fill'> {
  alt?: string
  data: Typo3Image
  objectFit?: boolean
  objectCover?: boolean
  naked?: boolean
  fill?: boolean
  imageClasseName?: string
  priority?: boolean
  hoverGroupEffect?: boolean
  crop?: {
    width: number
    height: number
  }
  ref?: React.RefObject<HTMLImageElement>
}

const Img = ({
  data,
  className,
  objectFit,
  objectCover,
  naked = false,
  fill = false,
  imageClasseName,
  unoptimized,
  priority,
  hoverGroupEffect,
  crop,
  ...props
}: ImgProps) => {
  const isMobile = useMedia(MEDIA.mobile)
  const isTablet = useMedia(MEDIA.tablet)

  const getCrop = (): string => {
    if (!data.isImageSanity || !crop) {
      return ''
    } else {
      return `&w=${crop.width * 3}&h=${crop.height * 3}`
    }
  }

  if (!data) {
    return null
  }
  const { publicUrl, properties } = data

  const resolvedPublicUrl = (() => {
    const variants = data.cropVariants
    if (!variants) {
      return publicUrl
    }
    if (isMobile && variants.mobile?.publicUrl) {
      return variants.mobile.publicUrl
    }

    if (!isMobile && isTablet && variants.tablet?.publicUrl) {
      return variants.tablet.publicUrl
    }
    return publicUrl
  })()
  const width = props.width || properties?.dimensions?.width || 1200
  const height = props.height || properties?.dimensions?.height || 0
  const rawAlt = (props.alt ?? properties?.alternative ?? '') as string
  const alt = rawAlt === 'decorative' ? '' : rawAlt
  const blurDataURL =
    properties?.lqip ||
    (properties?.blurHash && getBlurhash(properties.blurHash)) ||
    undefined
  const placeHolder = blurDataURL ? 'blur' : 'empty'

  const imageClasses = cn(
    objectFit && 'object-fit',
    objectCover && 'object-cover',
    hoverGroupEffect && 'image-hover-effect',
    naked && className,
    imageClasseName
  )
  const wrapClasses = cn('relative overflow-hidden', !naked && className)

  if (naked) {
    return (
      <Image
        src={`${resolvedPublicUrl}${getCrop()}`}
        alt={alt as string}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        className={imageClasses}
        blurDataURL={blurDataURL}
        placeholder={placeHolder}
        fill={fill}
        priority={priority || false}
        quality={props.quality || 80}
        unoptimized={!!unoptimized}
        {...props}
      />
    )
  } else {
    return (
      <Div className={wrapClasses} {...props}>
        <Skeleton className="absolute inset-0 -z-10" />
        <Image
          fill={fill}
          src={`${resolvedPublicUrl}${getCrop()}`}
          alt={alt as string}
          width={fill ? undefined : width}
          height={fill ? undefined : height}
          className={imageClasses}
          blurDataURL={blurDataURL}
          placeholder={placeHolder}
          priority={priority || false}
          sizes={props.sizes}
          quality={props.quality || 80}
          unoptimized={!!unoptimized}
        />
      </Div>
    )
  }
}

Img.displayName = 'Img'

export default memo(Img)
